import React, { PropsWithChildren, useState } from 'react';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { FormProvider } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import {
	deleteSecurityRedemptionRights,
	updateSecurityRedemptionRights,
} from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { DetailEditableField } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/DetailEditableField.tsx';
import {
	getPeriodUnit,
	makePeriodTimeUnitOptions,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/period-dropdown-options.tsx';
import {
	REDEMPTION_RIGHTS_FORM_FIELD,
	useRedemptionRightsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/RedemptionRights/useRedemptionRightsController.ts';

import { FormSegmentedControl } from '@dealstack/frontend/shared/components/FormSegmentedControl/FormSegmentedControl.tsx';
import { SecurityFeatureBaseProps } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import {
	RedemptionRightsType,
	SecurityRedemptionRightsFormDto,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/RedemptionRights/redemptionRights.zod.ts';
import { MixedTextDropdownInput } from '@dealstack/frontend/shared/components/MixedTextDropdownInput/MixedTextDropdownInput';
import { NOT_APPLICABLE } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { EditableInfoContainer } from '@dealstack/frontend/shared/components/EditableInfoContainer/EditableInfoContainer.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { EditableInfoContainerGrid } from '@dealstack/frontend/shared/components/EditableInfoContainer/styled.tsx';

export const RedemptionRights = ({
	security,
	onCancel,
	refreshDetails,
	hasEditorPermission = true,
}: PropsWithChildren<SecurityFeatureBaseProps>) => {
	const tCommonUI = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const REDEMPTION_RIGHT_TYPE_OPTIONS = [
		{
			value: RedemptionRightsType.PERPETUAL,
			label: tCapManage('securities.redemptionRights.type.perpetual'),
		},
		{
			value: RedemptionRightsType.FIXED,
			label: tCapManage('securities.redemptionRights.type.fixed'),
		},
	];

	const [isEdit, setIsEdit] = useState(!security?.securityRedemptionRights);

	const deleteRedemptionRightsRequest = useMutation({
		mutationFn: deleteSecurityRedemptionRights,
	});
	const updateRedemptionRightsRequest = useMutation({
		mutationFn: updateSecurityRedemptionRights,
	});

	const isLoading =
		updateRedemptionRightsRequest.isPending || deleteRedemptionRightsRequest.isPending;

	const handleUpdateRedemptionRights = (args: SecurityRedemptionRightsFormDto) => {
		const securityId = security?.id ?? '';
		updateRedemptionRightsRequest.mutate(
			{
				securityId,
				body: {
					term: args.term ?? null,
					termDuration: args.termDuration ? Number(args.termDuration) : null,
				},
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.redemption_rights_updated_successfully'),
					});

					refreshDetails?.(securityId);
					setIsEdit(false);
				},
			}
		);
	};

	const { redemptionRightsForm, resetForm, isTermDisabled, validateTermField } =
		useRedemptionRightsController(security, tCapManage);

	const handleDeleteRedemptionRights = () => {
		const securityId = security?.id ?? '';
		deleteRedemptionRightsRequest.mutate(securityId, {
			onSuccess: () => {
				showToastV2({
					title: tCapManage('toasts.success.redemption_rights_deleted_successfully'),
				});
				refreshDetails?.(securityId);
				setIsEdit(false);
			},
		});
	};

	const handleCancel = () => {
		resetForm();
		onCancel?.();
	};

	return (
		<EditableInfoContainer
			title={tCapManage('securities.redemptionRights.title')}
			isEdit={isEdit}
			setIsEdit={setIsEdit}
			disableButtons={isLoading}
			onUpdate={redemptionRightsForm.handleSubmit(handleUpdateRedemptionRights)}
			onRemove={handleDeleteRedemptionRights}
			onCancel={handleCancel}
			hideRemove={!security?.securityRedemptionRights?.id}
			hasEditorPermission={hasEditorPermission}
			updateBtnDataTestId={TEST_ELEMENT_IDS.CAP_SECURITY_DETAILS_FEATURE_ROW_UPDATE_BUTTON}
		>
			<FormProvider {...redemptionRightsForm}>
				{isLoading && <InlineLoader />}
				<form
					onSubmit={redemptionRightsForm.handleSubmit(handleUpdateRedemptionRights)}
					style={{ width: '100%' }}
				>
					<EditableInfoContainerGrid $gridColumns={4}>
						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.redemptionRights.type.label')}
							formInput={
								<FormSegmentedControl
									name={REDEMPTION_RIGHTS_FORM_FIELD.type}
									data={REDEMPTION_RIGHT_TYPE_OPTIONS}
									mt="0.25rem"
									onChange={validateTermField}
								/>
							}
							data={redemptionRightsForm.watch(REDEMPTION_RIGHTS_FORM_FIELD.type)}
						/>

						<DetailEditableField
							editMode={isEdit}
							label={tCapManage('securities.redemptionRights.term.label')}
							formInput={
								<MixedTextDropdownInput
									inputName={REDEMPTION_RIGHTS_FORM_FIELD.termDuration}
									selectName={REDEMPTION_RIGHTS_FORM_FIELD.term}
									control={redemptionRightsForm.control}
									selectData={makePeriodTimeUnitOptions(tCommonUI, true)}
									selectPlaceholder={NOT_APPLICABLE}
									disabled={isTermDisabled}
								/>
							}
							data={
								getPeriodUnit(
									tCommonUI,
									security?.securityRedemptionRights?.term,
									security?.securityRedemptionRights?.termDuration
								) ?? 'N/A'
							}
						/>
					</EditableInfoContainerGrid>
				</form>
			</FormProvider>
		</EditableInfoContainer>
	);
};
